import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Filters from './components/Filters';
import PetCard from './components/PetCard';
import SearchBar from './components/SearchBar';
import FilterTags from './components/FilterTags';
import Overview from './pages/Overview';
import MyPost from './pages/MyPost';
import Favourites from './pages/Favourites';
import Rehome from './pages/Rehome';
import Message from './pages/Message';

// Mock pet data
const mockPets = [
  {
    id: 1,
    name: 'Simba',
    breed: 'Rottweiler',
    age: '2 years',
    gender: 'Male',
    location: 'Mumbai, Maharashtra',
    image: 'https://images.unsplash.com/photo-1567752881298-894bb81f9379?w=400',
    type: 'Dog',
    postedBy: 'User',
    adoptOrRehome: 'Adopt'
  },
  {
    id: 2,
    name: 'Luna',
    breed: 'Persian Cat',
    age: '1 year',
    gender: 'Female',
    location: 'Delhi, India',
    image: 'https://images.unsplash.com/photo-1574158622682-e40e69881006?w=400',
    type: 'Cat',
    postedBy: 'NGO',
    adoptOrRehome: 'Adopt'
  },
  {
    id: 3,
    name: 'Max',
    breed: 'Golden Retriever',
    age: '3 years',
    gender: 'Male',
    location: 'Bangalore, Karnataka',
    image: 'https://images.unsplash.com/photo-1633722715463-d30f4f325e24?w=400',
    type: 'Dog',
    postedBy: 'User',
    adoptOrRehome: 'Rehome'
  },
  {
    id: 4,
    name: 'Bella',
    breed: 'Labrador',
    age: '1.5 years',
    gender: 'Female',
    location: 'Pune, Maharashtra',
    image: 'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=400',
    type: 'Dog',
    postedBy: 'NGO',
    adoptOrRehome: 'Adopt'
  },
  {
    id: 5,
    name: 'Whiskers',
    breed: 'Siamese Cat',
    age: '2 years',
    gender: 'Male',
    location: 'Chennai, Tamil Nadu',
    image: 'https://images.unsplash.com/photo-1513360371669-4adf3dd7dff8?w=400',
    type: 'Cat',
    postedBy: 'User',
    adoptOrRehome: 'Adopt'
  },
  {
    id: 6,
    name: 'Fluffy',
    breed: 'Dutch Rabbit',
    age: '6 months',
    gender: 'Female',
    location: 'Hyderabad, Telangana',
    image: 'https://images.unsplash.com/photo-1585110396000-c9ffd4e4b308?w=400',
    type: 'Rabbit',
    postedBy: 'NGO',
    adoptOrRehome: 'Adopt'
  },
  {
    id: 7,
    name: 'Rocky',
    breed: 'German Shepherd',
    age: '4 years',
    gender: 'Male',
    location: 'Kolkata, West Bengal',
    image: 'https://images.unsplash.com/photo-1568572933382-74d440642117?w=400',
    type: 'Dog',
    postedBy: 'User',
    adoptOrRehome: 'Rehome'
  },
  {
    id: 8,
    name: 'Mittens',
    breed: 'British Shorthair',
    age: '3 years',
    gender: 'Female',
    location: 'Ahmedabad, Gujarat',
    image: 'https://images.unsplash.com/photo-1596854407944-bf87f6fdd49e?w=400',
    type: 'Cat',
    postedBy: 'NGO',
    adoptOrRehome: 'Adopt'
  },
  {
    id: 9,
    name: 'Buddy',
    breed: 'Beagle',
    age: '2.5 years',
    gender: 'Male',
    location: 'Jaipur, Rajasthan',
    image: 'https://images.unsplash.com/photo-1505628346881-b72b27e84530?w=400',
    type: 'Dog',
    postedBy: 'User',
    adoptOrRehome: 'Adopt'
  }
];

function App() {
  const [filteredPets, setFilteredPets] = useState(mockPets);
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [activeSection, setActiveSection] = useState('Overview');
  const [darkMode, setDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    adoptOrRehome: 'Adopt',
    petType: [],
    gender: [],
    breed: []
  });

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
    
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode) {
      setDarkMode(JSON.parse(savedDarkMode));
    }

    // Simulate loading
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  // Save favorites to localStorage
  useEffect(() => {
    localStorage.setItem('favorites', JSON.stringify(favorites));
  }, [favorites]);

  // Save dark mode preference
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // Filter pets based on search and filters
  useEffect(() => {
    let result = mockPets;

    // Search filter
    if (searchQuery) {
      result = result.filter(pet =>
        pet.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pet.breed.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Adopt/Rehome filter
    if (filters.adoptOrRehome) {
      result = result.filter(pet => pet.adoptOrRehome === filters.adoptOrRehome);
    }

    // Pet type filter
    if (filters.petType.length > 0) {
      result = result.filter(pet => filters.petType.includes(pet.type));
    }

    // Gender filter
    if (filters.gender.length > 0) {
      result = result.filter(pet => filters.gender.includes(pet.gender));
    }

    // Breed filter
    if (filters.breed.length > 0) {
      result = result.filter(pet => filters.breed.includes(pet.breed));
    }

    setFilteredPets(result);
  }, [searchQuery, filters]);

  const toggleFavorite = (petId) => {
    setFavorites(prev =>
      prev.includes(petId)
        ? prev.filter(id => id !== petId)
        : [...prev, petId]
    );
  };

  const handleAdoptNow = (pet) => {
    console.log(`Adopt Now clicked for: ${pet.breed} - ${pet.name}`);
  };

  const updateFilter = (filterType, value) => {
    setFilters(prev => {
      if (filterType === 'adoptOrRehome') {
        return { ...prev, [filterType]: value };
      }
      
      const currentValues = prev[filterType];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      
      return { ...prev, [filterType]: newValues };
    });
  };

  const removeFilter = (filterType, value) => {
    if (filterType === 'adoptOrRehome') {
      return;
    }
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].filter(v => v !== value)
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      adoptOrRehome: 'Adopt',
      petType: [],
      gender: [],
      breed: []
    });
    setSearchQuery('');
  };

  const getActiveFilters = () => {
    const active = [];
    
    filters.petType.forEach(type => active.push({ type: 'petType', value: type, label: type }));
    filters.gender.forEach(gender => active.push({ type: 'gender', value: gender, label: gender }));
    filters.breed.forEach(breed => active.push({ type: 'breed', value: breed, label: breed }));
    
    return active;
  };

  // Adopt page component
  const AdoptPage = () => (
    <div className="content-area">
      <div className="content-wrapper">
        <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        
        <FilterTags
          activeFilters={getActiveFilters()}
          removeFilter={removeFilter}
          clearAllFilters={clearAllFilters}
        />

        <div className="pets-section">
          {isLoading ? (
            <div className="pets-grid">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="skeleton-card">
                  <div className="skeleton-image"></div>
                  <div className="skeleton-content">
                    <div className="skeleton-line"></div>
                    <div className="skeleton-line short"></div>
                    <div className="skeleton-line"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPets.length > 0 ? (
            <div className="pets-grid">
              {filteredPets.map(pet => (
                <PetCard
                  key={pet.id}
                  pet={pet}
                  isFavorite={favorites.includes(pet.id)}
                  toggleFavorite={toggleFavorite}
                  handleAdoptNow={handleAdoptNow}
                />
              ))}
            </div>
          ) : (
            <div className="no-results">
              <h2>No pets found</h2>
              <p>Try adjusting your filters or search query</p>
            </div>
          )}
        </div>
      </div>

      <Filters filters={filters} updateFilter={updateFilter} clearAllFilters={clearAllFilters} />
    </div>
  );

  return (
    <Router>
      <div className={`App ${darkMode ? 'dark-mode' : ''}`}>
        <Header darkMode={darkMode} setDarkMode={setDarkMode} />
        
        <div className="main-container">
          <Sidebar activeSection={activeSection} setActiveSection={setActiveSection} />
          
          <Routes>
            <Route path="/" element={<Navigate to="/overview" replace />} />
            <Route path="/overview" element={<Overview />} />
            <Route path="/my-post" element={<MyPost />} />
            <Route path="/favourites" element={<Favourites />} />
            <Route path="/adopt" element={<AdoptPage />} />
            <Route path="/rehome" element={<Rehome />} />
            <Route path="/message" element={<Message />} />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;
